import { GROUP_NAME, type GroupSummary } from '@geosport/shared';
import { useState, type FormEvent } from 'react';
import { Button } from '../components/Button';
import { Shell } from '../components/Shell';
import { track } from '../lib/analytics';
import { api, ApiError } from '../lib/api';
import { Link, navigate } from '../lib/router';
import { useApi } from '../lib/useApi';

export function Groups() {
  const { data, error, loading, reload } = useApi<{ groups: GroupSummary[] }>('/groups');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      const g = await api<GroupSummary>('/groups', { method: 'POST', body: { name } });
      track('group_created');
      navigate(`/groups/${g.id}?new=1`);
    } catch (err) {
      setMessage(err instanceof ApiError ? err.message : 'Could not create the group');
      setBusy(false);
    }
  };

  const join = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      const g = await api<GroupSummary>('/groups/join', { method: 'POST', body: { code } });
      track('group_joined');
      navigate(`/groups/${g.id}`);
    } catch (err) {
      setMessage(err instanceof ApiError ? err.message : 'Could not join the group');
      setBusy(false);
    }
  };

  return (
    <Shell title="Groups">
      <p className="mb-4 text-sm text-white/60">
        A group is a private leaderboard for your friends, family or office. Share the invite link
        and compare scores every day. No sign-up needed.
      </p>

      {error && (
        <div className="mb-4 rounded-2xl bg-red-500/10 p-4 text-sm text-red-200 ring-1 ring-red-500/30">
          <p className="mb-2">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="min-h-11 rounded-lg bg-white/10 px-4 font-semibold"
          >
            Retry
          </button>
        </div>
      )}
      {loading && !data && <div className="skeleton mb-4 h-16 rounded-2xl" aria-hidden="true" />}

      {data && data.groups.length > 0 && (
        <ol
          className="mb-6 divide-y divide-white/5 rounded-2xl bg-white/5"
          data-testid="group-list"
        >
          {data.groups.map((g) => (
            <li key={g.id}>
              <Link
                to={`/groups/${g.id}`}
                className="flex min-h-14 items-center gap-3 px-3 py-2 hover:bg-white/5"
              >
                <div className="flex-1">
                  <div className="font-semibold">{g.name}</div>
                  <div className="text-xs text-white/50">
                    {g.members} {g.members === 1 ? 'member' : 'members'} · code {g.code}
                    {g.isOwner && ' · owner'}
                  </div>
                </div>
                <span className="text-white/40">›</span>
              </Link>
            </li>
          ))}
        </ol>
      )}

      <form
        onSubmit={(e) => void create(e)}
        className="mb-4 rounded-2xl bg-ink-2 p-4 ring-1 ring-white/10"
      >
        <label htmlFor="group-name" className="mb-1 block font-semibold">
          Create a group
        </label>
        <div className="flex gap-2">
          <input
            id="group-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            minLength={GROUP_NAME.min}
            maxLength={GROUP_NAME.max}
            placeholder="e.g. Sunday League"
            className="min-h-11 flex-1 rounded-lg bg-white/10 px-3 text-white placeholder:text-white/30"
          />
          <Button type="submit" disabled={busy || name.trim().length < GROUP_NAME.min}>
            Create
          </Button>
        </div>
      </form>

      <form
        onSubmit={(e) => void join(e)}
        className="rounded-2xl bg-ink-2 p-4 ring-1 ring-white/10"
      >
        <label htmlFor="group-code" className="mb-1 block font-semibold">
          Join with a code
        </label>
        <div className="flex gap-2">
          <input
            id="group-code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            maxLength={8}
            placeholder="ABC123"
            autoCapitalize="characters"
            className="min-h-11 flex-1 rounded-lg bg-white/10 px-3 font-mono tracking-widest text-white placeholder:text-white/30"
          />
          <Button type="submit" variant="secondary" disabled={busy || code.trim().length < 6}>
            Join
          </Button>
        </div>
      </form>
      {message && (
        <p className="mt-3 text-sm text-red-300" role="status">
          {message}
        </p>
      )}
    </Shell>
  );
}
